"use client";

import { useEffect, useLayoutEffect, useId, useRef, useState, type ReactNode } from "react";
import { animateWindowToDock } from "./animateWindowToDock";

export default function FloatingConsoleWindow({
  title, label, visible, front, workspace = false, app, onActivate, onMinimize, onClose, onEscape, children,
}: {
  title: string; label: string; visible: boolean; front: boolean; workspace?: boolean; app?: "files" | "pdf" | "browser";
  onActivate: () => void; onMinimize: () => void; onClose: () => void;
  onEscape: () => void; children: ReactNode;
}) {
  const id = useId();
  const frame = useRef<HTMLElement>(null);
  const drag = useRef<{ x: number; y: number; left: number; top: number; lastX: number; lastY: number; time: number; vx: number; vy: number } | null>(null);
  const glide = useRef<number | null>(null);
  const stopGlide = () => {
    if (glide.current !== null) cancelAnimationFrame(glide.current);
    glide.current = null;
    if (frame.current) delete frame.current.dataset.windowGlide;
  };
  useEffect(() => () => stopGlide(), []);
  const [present, setPresent] = useState(visible);
  const motion = useRef<(() => void) | null>(null);
  const previousVisible = useRef(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    const fit = () => {
      const element = frame.current;
      if (!element || !visible || element.dataset.windowMotion) return;
      stopGlide();
      const rect = element.getBoundingClientRect();
      const x = Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8));
      const y = Math.max(8, Math.min(rect.top, window.innerHeight - rect.height - 64));
      if (x !== rect.left || y !== rect.top) setPosition({ x, y });
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [visible]);

  useLayoutEffect(() => {
    const element = frame.current;
    stopGlide();
    element?.toggleAttribute("inert", !visible);
    if (!element || (visible === previousVisible.current && !motion.current)) return;
    previousVisible.current = visible;
    motion.current?.();
    motion.current = null;
    if (visible) setPresent(true);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dock = document.querySelector<HTMLElement>(`[data-console-dock="${label}"]`);
    if (reduced || !dock) {
      element.style.visibility = "";
      if (!visible) element.hidden = true;
      setPresent(visible);
      return;
    }
    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) { setPresent(visible); return; }
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8));
    const top = Math.max(8, Math.min(rect.top, window.innerHeight - rect.height - 64));
    if (visible && (left !== rect.left || top !== rect.top)) setPosition({ x: left, y: top });
    // Settle the resting coordinates before measuring the visual slices.
    if (visible && (left !== rect.left || top !== rect.top)) {
      element.style.left = "0px";
      element.style.top = "0px";
      element.style.translate = `${left}px ${top}px`;
    }
    element.dataset.windowMotion = visible ? "restoring" : "minimizing";
    const cleanup = animateWindowToDock(element, dock, visible, () => {
      // Hide before dropping the visual: React's state update can otherwise
      // leave a single frame of the full-size window on screen.
      if (!visible) element.hidden = true;
      setPresent(visible);
      element.style.visibility = "";
      delete element.dataset.windowMotion;
      cleanup();
      motion.current = null;
      if (visible && element.classList.contains("is-front")) {
        const target = element.querySelector<HTMLElement>("input") ?? element.querySelector<HTMLElement>(".app-content");
        target?.focus({ preventScroll: true });
      }
    });
    motion.current = cleanup;
    return () => {
      cleanup();
      element.style.visibility = "";
      delete element.dataset.windowMotion;
      motion.current = null;
    };
  }, [visible, label]);

  return <section ref={frame} role="dialog" aria-modal="false" aria-labelledby={id}
    hidden={!visible && !present}
    className={`sahi-console floating-console ${app ? `floating-${app} floating-app console-mode-gui` : workspace ? "floating-workspace console-mode-gui" : "floating-terminal console-mode-text"} ${front ? "is-front" : ""}`}
    style={position ? { left: 0, top: 0, translate: `${position.x}px ${position.y}px` } : undefined}
    data-lenis-prevent
    onPointerDownCapture={onActivate}
    onFocusCapture={onActivate}
    onKeyDown={event => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault(); event.stopPropagation(); onEscape();
      }
    }}>
    <div className={`console-window${workspace ? "" : " console-shell-window"}`}>
      <header className="console-top floating-titlebar" tabIndex={0}
        aria-label={`Move ${label} window with arrow keys`}
        onKeyDown={event => {
          if (event.target !== event.currentTarget || !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) return;
          event.preventDefault();
          stopGlide();
          const rect = frame.current!.getBoundingClientRect();
          const step = event.shiftKey ? 40 : 10;
          setPosition({
            x: Math.max(8, Math.min(rect.left + (event.key === "ArrowRight" ? step : event.key === "ArrowLeft" ? -step : 0), window.innerWidth - rect.width - 8)),
            y: Math.max(8, Math.min(rect.top + (event.key === "ArrowDown" ? step : event.key === "ArrowUp" ? -step : 0), window.innerHeight - 52)),
          });
        }}
        onPointerDown={event => {
          if (event.button !== 0 || (event.target as HTMLElement).closest("button")) return;
          stopGlide();
          const rect = frame.current!.getBoundingClientRect();
          drag.current = { x: event.clientX, y: event.clientY, left: rect.left, top: rect.top,
            lastX: event.clientX, lastY: event.clientY, time: event.timeStamp, vx: 0, vy: 0 };
          event.currentTarget.setPointerCapture(event.pointerId);
          event.preventDefault();
        }}
        onPointerMove={event => {
          if (!drag.current || !frame.current) return;
          const rect = frame.current.getBoundingClientRect();
          const dt = Math.max(1, event.timeStamp - drag.current.time);
          const weight = Math.min(1, dt / 40);
          drag.current.vx = drag.current.vx * (1 - weight) + ((event.clientX - drag.current.lastX) / dt) * weight;
          drag.current.vy = drag.current.vy * (1 - weight) + ((event.clientY - drag.current.lastY) / dt) * weight;
          drag.current.lastX = event.clientX;
          drag.current.lastY = event.clientY;
          drag.current.time = event.timeStamp;
          setPosition({
            x: Math.max(8, Math.min(drag.current.left + event.clientX - drag.current.x, window.innerWidth - rect.width - 8)),
            y: Math.max(8, Math.min(drag.current.top + event.clientY - drag.current.y, window.innerHeight - 52)),
          });
        }}
        onPointerUp={event => {
          const released = drag.current;
          drag.current = null;
          if (!released || !frame.current || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
          const stale = Math.exp(-Math.max(0, event.timeStamp - released.time) / 60);
          let vx = Math.max(-2, Math.min(2, released.vx * stale));
          let vy = Math.max(-2, Math.min(2, released.vy * stale));
          if (Math.hypot(vx, vy) < .12) return;
          const rect = frame.current.getBoundingClientRect();
          let x = rect.left, y = rect.top;
          let previous = performance.now();
          frame.current.dataset.windowGlide = "true";
          const coast = (now: number) => {
            const dt = Math.min(32, now - previous);
            previous = now;
            const decay = Math.exp(-dt / 90);
            const nextX = Math.max(8, Math.min(x + vx * 90 * (1 - decay), window.innerWidth - rect.width - 8));
            const nextY = Math.max(8, Math.min(y + vy * 90 * (1 - decay), window.innerHeight - 52));
            vx = nextX === 8 || nextX === window.innerWidth - rect.width - 8 ? 0 : vx * decay;
            vy = nextY === 8 || nextY === window.innerHeight - 52 ? 0 : vy * decay;
            x = nextX; y = nextY;
            setPosition({ x, y });
            if (Math.hypot(vx, vy) > .015) glide.current = requestAnimationFrame(coast);
            else stopGlide();
          };
          glide.current = requestAnimationFrame(coast);
        }}
        onPointerCancel={() => { drag.current = null; stopGlide(); }}
        onLostPointerCapture={() => { drag.current = null; }}>
        <div className="console-identity">
          <span className="console-signal" aria-hidden="true" />
          <h2 id={id}>{title}</h2>
          <span className="console-local">{workspace ? "DESKTOP SESSION" : "TERMINAL"}</span>
        </div>
        <div className="floating-window-controls">
          <button className="console-close" aria-label={`Minimize ${label}`} onClick={onMinimize}>−</button>
          <button className="console-close" aria-label={`Close ${label}`} onClick={onClose}>×</button>
        </div>
      </header>
      {children}
    </div>
  </section>;
}
